import type { EpocusEncounter } from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { useMemo, useState } from "react";
import { formatDicomPatientName } from "./dicom-patient-name";

export const RECENT_ENCOUNTERS_INITIAL = 5;
export const RECENT_ENCOUNTERS_STEP = 10;

// Fields carried on the encounter doc that a performer would search by. The
// device/patient identifiers are the only identity fields synced onto the
// encounter; indication and exam types live on the separate exam collection.
const searchableText = (encounter: EpocusEncounter): string =>
  [
    encounter.patientName,
    formatDicomPatientName(encounter.patientName),
    encounter.elephantPatientId,
    encounter.accessionNumber,
    encounter.location,
    encounter.deviceId,
  ]
    .filter((field): field is string => Boolean(field))
    .join(" ")
    .toLowerCase();

const matchesSearch = (encounter: EpocusEncounter, term: string): boolean =>
  searchableText(encounter).includes(term);

type UseRecentEncountersArgs = {
  encounters: EpocusEncounter[];
  searchTerm: string;
};

type UseRecentEncountersResult = {
  visible: EpocusEncounter[];
  total: number;
  hasMore: boolean;
  showMore: () => void;
};

export const useRecentEncounters = ({
  encounters,
  searchTerm,
}: UseRecentEncountersArgs): UseRecentEncountersResult => {
  const normalisedTerm = searchTerm.trim().toLowerCase();

  const matching = useMemo(() => {
    const filtered = encounters.filter(
      (encounter) =>
        encounter.status !== "imported" &&
        (normalisedTerm === "" || matchesSearch(encounter, normalisedTerm)),
    );
    return filtered.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [encounters, normalisedTerm]);

  const [visibleCount, setVisibleCount] = useState(RECENT_ENCOUNTERS_INITIAL);
  const [appliedTerm, setAppliedTerm] = useState(normalisedTerm);

  // Reset the window when the search term changes (React's recommended
  // adjust-state-during-render pattern, avoiding an effect + extra paint).
  if (normalisedTerm !== appliedTerm) {
    setAppliedTerm(normalisedTerm);
    setVisibleCount(RECENT_ENCOUNTERS_INITIAL);
  }

  const showMore = () =>
    setVisibleCount((count) => count + RECENT_ENCOUNTERS_STEP);

  return {
    visible: matching.slice(0, visibleCount),
    total: matching.length,
    hasMore: visibleCount < matching.length,
    showMore,
  };
};
